import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import { usersAPI, locationsAPI } from '@/lib/api';
import { ROLE_LABELS, ROLE_LEVELS } from '@/lib/roles';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Plus, Search, MoreHorizontal, KeyRound, UserX, UserCheck, Trash2, Loader2, ArrowUpDown, ArrowUp, ArrowDown, Pencil, Download } from 'lucide-react';
import LocationsSelector from './LocationsSelector';

// Format a last_login ISO string into Dutch human-readable form.
// Returns { label, tone } where tone is 'ok' | 'warn' | 'danger' | 'never'.
function formatLastLogin(iso) {
  if (!iso) return { label: 'Nog nooit ingelogd', tone: 'never' };
  const d = new Date(iso);
  if (isNaN(d.getTime())) return { label: 'Nog nooit ingelogd', tone: 'never' };
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');

  const sameDay = d.toDateString() === now.toDateString();
  const yest = new Date(now); yest.setDate(now.getDate() - 1);
  const isYesterday = d.toDateString() === yest.toDateString();

  let label;
  if (sameDay) label = `Vandaag om ${hh}:${mm}`;
  else if (isYesterday) label = `Gisteren om ${hh}:${mm}`;
  else if (diffDays < 7) label = `${diffDays} ${diffDays === 1 ? 'dag' : 'dagen'} geleden`;
  else {
    const dd = String(d.getDate()).padStart(2, '0');
    const mo = String(d.getMonth() + 1).padStart(2, '0');
    label = `${dd}-${mo}-${d.getFullYear()}, ${hh}:${mm}`;
  }

  let tone = 'ok';
  if (diffDays > 30) tone = 'danger';
  else if (diffDays >= 7) tone = 'warn';
  return { label, tone };
}

export default function UsersTab() {
  const { user: currentUser, hasMinRole } = useAuth();
  const [users, setUsers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [locationFilter, setLocationFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deactivateTarget, setDeactivateTarget] = useState(null);
  const [actionLoading, setActionLoading] = useState('');
  const [loginSort, setLoginSort] = useState('none'); // 'none' | 'desc' | 'asc'

  const loadUsers = useCallback(async () => {
    try {
      const res = await usersAPI.list();
      if (res.ok) setUsers(await res.clone().json());
    } catch {} finally {
      setLoading(false);
    }
  }, []);

  const loadLocations = useCallback(async () => {
    try {
      const res = await locationsAPI.list();
      if (res.ok) setLocations(await res.clone().json());
    } catch { /* tolerate; selector falls back to empty */ }
  }, []);

  useEffect(() => { loadUsers(); loadLocations(); }, [loadUsers, loadLocations]);

  const canManage = (targetRole) => {
    const myLevel = ROLE_LEVELS[currentUser?.globale_rol] || 0;
    const targetLevel = ROLE_LEVELS[targetRole] || 0;
    return myLevel > targetLevel;
  };

  const filteredUsers = users.filter(u => {
    const matchSearch = !search || u.naam.toLowerCase().includes(search.toLowerCase()) || u.email.toLowerCase().includes(search.toLowerCase());
    const matchRole = roleFilter === 'all' || u.globale_rol === roleFilter;
    const matchLocation = locationFilter === 'all' || (Array.isArray(u.locations) && u.locations.includes(locationFilter));
    const matchStatus = statusFilter === 'all' || u.status === statusFilter;
    return matchSearch && matchRole && matchLocation && matchStatus;
  });

  const sortedUsers = (() => {
    if (loginSort === 'none') return filteredUsers;
    const toTs = (iso) => {
      if (!iso) return null;
      const t = new Date(iso).getTime();
      return isNaN(t) ? null : t;
    };
    const arr = [...filteredUsers];
    arr.sort((a, b) => {
      const ta = toTs(a.last_login);
      const tb = toTs(b.last_login);
      // Never-logged-in users always at the bottom regardless of direction
      if (ta === null && tb === null) return 0;
      if (ta === null) return 1;
      if (tb === null) return -1;
      return loginSort === 'desc' ? tb - ta : ta - tb;
    });
    return arr;
  })();

  const cycleLoginSort = () => {
    setLoginSort(prev => prev === 'none' ? 'desc' : prev === 'desc' ? 'asc' : 'none');
  };

  // --- CSV export helpers ----------------------------------------------
  // Export uses the currently-filtered + sorted list so admins can narrow
  // first, then download a targeted set. Client-side only — no backend hit.
  const csvEscape = (val) => {
    if (val === null || val === undefined) return '';
    const s = String(val);
    // Wrap values containing separator / quote / newline and double-up quotes.
    return /[";\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };

  const formatCsvDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, '0');
    const mi = String(d.getMinutes()).padStart(2, '0');
    return `${dd}-${mm}-${yy} ${hh}:${mi}`;
  };

  const handleExportCsv = () => {
    const rows = sortedUsers;
    if (rows.length === 0) {
      toast.error('Geen gebruikers om te exporteren (check filters)');
      return;
    }
    const header = ['Naam', 'E-mail', 'Rol', 'Status', 'Telefoon', 'Locaties', 'Laatste login', 'Aangemaakt op'];
    const lines = [header.join(';')];
    for (const u of rows) {
      const locs = Array.isArray(u.locations) ? u.locations.join(', ') : '';
      lines.push([
        csvEscape(u.naam),
        csvEscape(u.email),
        csvEscape(ROLE_LABELS[u.globale_rol] || u.globale_rol),
        csvEscape(u.status === 'actief' ? 'Actief' : 'Inactief'),
        csvEscape(u.telefoon || ''),
        csvEscape(locs),
        csvEscape(formatCsvDate(u.last_login)),
        csvEscape(formatCsvDate(u.created_at)),
      ].join(';'));
    }
    // UTF-8 BOM so Dutch Excel renders é / ë / ü correctly.
    const blob = new Blob(['\uFEFF' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const today = new Date().toISOString().slice(0, 10);
    const link = document.createElement('a');
    link.href = url;
    link.download = `tw1-gebruikers-${today}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success(`${rows.length} gebruiker${rows.length === 1 ? '' : 's'} geëxporteerd`);
  };

  const handleResetPw = async (userId, userName) => {
    setActionLoading(userId);
    try {
      const res = await usersAPI.resetPassword(userId);
      if (res.ok) toast.success(`Wachtwoord reset verzonden voor ${userName}`);
      else { const d = await res.clone().json(); toast.error(d.detail || 'Fout'); }
    } catch { toast.error('Fout bij wachtwoord reset'); }
    setActionLoading('');
  };

  const handleDeactivate = async () => {
    if (!deactivateTarget) return;
    setActionLoading(deactivateTarget.id);
    try {
      const res = deactivateTarget.status === 'actief'
        ? await usersAPI.deactivate(deactivateTarget.id)
        : await usersAPI.activate(deactivateTarget.id);
      if (res.ok) {
        toast.success(deactivateTarget.status === 'actief' ? 'Gebruiker gedeactiveerd' : 'Gebruiker geactiveerd');
        loadUsers();
      } else { const d = await res.clone().json(); toast.error(d.detail || 'Fout'); }
    } catch { toast.error('Fout'); }
    setActionLoading('');
    setDeactivateTarget(null);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setActionLoading(deleteTarget.id);
    try {
      const res = await usersAPI.delete(deleteTarget.id);
      if (res.ok) { toast.success('Gebruiker verwijderd'); loadUsers(); }
      else { const d = await res.clone().json(); toast.error(d.detail || 'Fout'); }
    } catch { toast.error('Fout bij verwijderen'); }
    setActionLoading('');
    setDeleteTarget(null);
  };

  return (
    <div>
      {/* Top actions */}
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between mb-4">
        <div className="flex flex-1 gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[180px] max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Zoeken..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9 h-9"
              data-testid="users-search-input"
            />
          </div>
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="w-[140px] h-9" data-testid="users-role-filter-select">
              <SelectValue placeholder="Alle rollen" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle rollen</SelectItem>
              {Object.entries(ROLE_LABELS).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={locationFilter} onValueChange={setLocationFilter}>
            <SelectTrigger className="w-[170px] h-9" data-testid="users-location-filter-select">
              <SelectValue placeholder="Alle locaties" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle locaties</SelectItem>
              {locations.map(l => (
                <SelectItem key={l.slug} value={l.slug}>{l.display_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[130px] h-9" data-testid="users-status-filter-select">
              <SelectValue placeholder="Alle statussen" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle statussen</SelectItem>
              <SelectItem value="actief">Alleen actief</SelectItem>
              <SelectItem value="inactief">Alleen inactief</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {hasMinRole('admin') && (
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleExportCsv}
              className="rounded-xl border-slate-200 text-[#1a2332] hover:bg-slate-50"
              data-testid="users-export-csv-button"
              title="Exporteer huidige selectie als CSV"
            >
              <Download className="w-4 h-4 mr-1.5" /> Exporteer CSV
            </Button>
            <Button
              onClick={() => setShowAddDialog(true)}
              className="bg-[#1a2332] hover:bg-[#111827] text-white rounded-xl btn-press transition-colors duration-150"
              data-testid="users-add-user-button"
            >
              <Plus className="w-4 h-4 mr-1.5" /> Gebruiker toevoegen
            </Button>
          </div>
        )}
      </div>

      {/* Users table */}
      <Card className="border border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-4 space-y-3">
              {[1,2,3].map(i => <Skeleton key={i} className="h-12 w-full" />)}
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-sm text-slate-500">Geen gebruikers gevonden</p>
            </div>
          ) : (
            <Table data-testid="users-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Gebruiker</TableHead>
                  <TableHead className="hidden sm:table-cell">E-mail</TableHead>
                  <TableHead>Rol</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="hidden md:table-cell whitespace-nowrap">
                    <button
                      type="button"
                      onClick={cycleLoginSort}
                      className="inline-flex items-center gap-1 hover:text-[#1a2332] transition-colors"
                      data-testid="users-last-login-sort-button"
                      title="Sorteren op laatste login"
                    >
                      Laatste login
                      {loginSort === 'none' && <ArrowUpDown className="w-3.5 h-3.5 opacity-60" />}
                      {loginSort === 'desc' && <ArrowDown className="w-3.5 h-3.5 text-[#d4a853]" />}
                      {loginSort === 'asc' && <ArrowUp className="w-3.5 h-3.5 text-[#d4a853]" />}
                    </button>
                  </TableHead>
                  <TableHead className="text-right">Acties</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedUsers.map(u => {
                  const login = formatLastLogin(u.last_login);
                  const isInactief = u.status !== 'actief';
                  return (
                  <TableRow key={u.id} data-testid={isInactief ? `users-row-inactive-${u.id}` : `users-row-${u.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold text-white flex-shrink-0 ${isInactief ? 'opacity-50' : ''}`}
                          style={{ backgroundColor: u.avatar_kleur || '#1a2332' }}
                        >
                          {u.naam?.charAt(0)?.toUpperCase()}
                        </div>
                        <div>
                          <p className={`font-medium text-sm ${isInactief ? 'text-slate-400' : 'text-[#1a2332]'}`}>{u.naam}</p>
                          <p className={`text-xs sm:hidden ${isInactief ? 'text-slate-300' : 'text-slate-500'}`}>{u.email}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className={`hidden sm:table-cell text-sm ${isInactief ? 'text-slate-400' : 'text-slate-600'}`}>{u.email}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`text-xs ${isInactief ? 'text-slate-400 border-slate-200' : ''}`}>
                        {ROLE_LABELS[u.globale_rol] || u.globale_rol}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={u.status === 'actief'
                        ? 'bg-emerald-50 text-emerald-700 border-emerald-100 hover:bg-emerald-50'
                        : 'bg-slate-100 text-slate-500 border-slate-200 hover:bg-slate-100'
                      }>
                        {u.status === 'actief' ? 'Actief' : 'Inactief'}
                      </Badge>
                    </TableCell>
                    <TableCell className="hidden md:table-cell whitespace-nowrap" data-testid={`users-last-login-cell-${u.id}`}>
                      <div className="flex items-center gap-2">
                        <span className={`text-sm ${login.tone === 'never' ? 'text-slate-400 italic' : 'text-slate-600'}`}>
                          {login.label}
                        </span>
                        {login.tone === 'danger' && (
                          <Badge className="bg-red-50 text-red-700 border-red-100 hover:bg-red-50 text-[10px] px-1.5 py-0" title="Meer dan 30 dagen geleden ingelogd">
                            30d+
                          </Badge>
                        )}
                        {login.tone === 'warn' && (
                          <Badge className="bg-amber-50 text-amber-700 border-amber-100 hover:bg-amber-50 text-[10px] px-1.5 py-0" title="7 tot 30 dagen geleden ingelogd">
                            7d+
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      {canManage(u.globale_rol) && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8" data-testid="users-row-actions-menu">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => setEditTarget(u)} data-testid={`users-edit-item-${u.id}`}>
                              <Pencil className="w-4 h-4 mr-2" /> Bewerken
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleResetPw(u.id, u.naam)} disabled={actionLoading === u.id}>
                              <KeyRound className="w-4 h-4 mr-2" /> Wachtwoord resetten
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setDeactivateTarget(u)}>
                              {u.status === 'actief'
                                ? <><UserX className="w-4 h-4 mr-2" /> Deactiveren</>
                                : <><UserCheck className="w-4 h-4 mr-2" /> Activeren</>
                              }
                            </DropdownMenuItem>
                            {u.globale_rol !== 'super_owner' && (
                              <DropdownMenuItem onClick={() => setDeleteTarget(u)} className="text-red-600">
                                <Trash2 className="w-4 h-4 mr-2" /> Verwijderen
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add User Dialog */}
      <AddUserDialog
        open={showAddDialog}
        onClose={() => setShowAddDialog(false)}
        onSuccess={() => { setShowAddDialog(false); loadUsers(); }}
        currentUserRole={currentUser?.globale_rol}
        locations={locations}
      />

      {/* Edit User Dialog */}
      <EditUserDialog
        open={!!editTarget}
        user={editTarget}
        onClose={() => setEditTarget(null)}
        onSuccess={() => { setEditTarget(null); loadUsers(); }}
        currentUserRole={currentUser?.globale_rol}
        locations={locations}
      />

      {/* Deactivate confirm */}
      <AlertDialog open={!!deactivateTarget} onOpenChange={() => setDeactivateTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {deactivateTarget?.status === 'actief' ? 'Gebruiker deactiveren?' : 'Gebruiker activeren?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deactivateTarget?.status === 'actief'
                ? `${deactivateTarget?.naam} kan niet meer inloggen na deactivering. Alle sessies worden beëindigd.`
                : `${deactivateTarget?.naam} kan weer inloggen na activering.`
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuleren</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeactivate}>
              {deactivateTarget?.status === 'actief' ? 'Deactiveren' : 'Activeren'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Gebruiker verwijderen?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget?.naam} ({deleteTarget?.email}) wordt permanent verwijderd. Dit kan niet ongedaan worden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuleren</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Verwijderen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function AddUserDialog({ open, onClose, onSuccess, currentUserRole, locations }) {
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: '', naam: '', password: '', globale_rol: 'locatie', locations: [], auth_method: 'email', telefoon: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const myLevel = ROLE_LEVELS[currentUserRole] || 0;
  const allowedRoles = Object.entries(ROLE_LABELS).filter(([k]) => ROLE_LEVELS[k] < myLevel);

  const validatePassword = (password) => {
    if (password.length < 8) return false;
    const hasUpper = /[A-Z]/.test(password);
    const hasLower = /[a-z]/.test(password);
    const hasDigit = /[0-9]/.test(password);
    return hasUpper && hasLower && hasDigit;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!validatePassword(form.password)) {
      setError('Wachtwoord moet minimaal 8 tekens bevatten met minstens 1 hoofdletter, 1 kleine letter en 1 cijfer.');
      return;
    }
    setLoading(true);
    try {
      // Trim telefoon and only send when non-empty (backend stores empty as null).
      const payload = { ...form };
      if (payload.telefoon) payload.telefoon = payload.telefoon.trim();
      if (!payload.telefoon) delete payload.telefoon;
      const res = await usersAPI.create(payload);
      if (res.ok) {
        const created = await res.clone().json().catch(() => null);
        const isAdminOrLower = ['admin', 'locatie', 'kluisbeheer'].includes(form.globale_rol);
        // For admin / locatie / kluisbeheer the user has NO tool access yet —
        // surface a Dutch toast with a quick-link to the Rechten tab so the
        // beheerder doesn't forget. super_owner / owner auto-bypass the
        // gatekeeper so the hint is not relevant for them.
        if (isAdminOrLower) {
          toast.success(`Gebruiker ${form.naam} aangemaakt`, {
            description: 'Vergeet niet tool-toegang in te stellen via Rechten.',
            action: {
              label: 'Naar Rechten →',
              onClick: () => navigate(`/instellingen?tab=rechten${created?.id ? `&newUser=${created.id}` : ''}`),
            },
            duration: 8000,
          });
        } else {
          toast.success(`Gebruiker ${form.naam} aangemaakt`);
        }
        setForm({ email: '', naam: '', password: '', globale_rol: 'locatie', locations: [], auth_method: 'email', telefoon: '' });
        onSuccess();
      } else {
        const d = await res.clone().json();
        setError(d.detail || 'Fout bij aanmaken');
      }
    } catch { setError('Fout bij aanmaken'); }
    setLoading(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="users-add-dialog">
        <DialogHeader>
          <DialogTitle>Gebruiker toevoegen</DialogTitle>
          <DialogDescription className="sr-only">
            Vul naam, e-mail, wachtwoord, rol, optioneel telefoon en eventuele locaties in.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && <p className="text-sm text-red-600 bg-red-50 p-2 rounded" data-testid="users-add-error">{error}</p>}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Naam</Label>
              <Input value={form.naam} onChange={e => setForm({...form, naam: e.target.value})} required data-testid="users-add-naam" />
            </div>
            <div className="space-y-2">
              <Label>E-mailadres</Label>
              <Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} required data-testid="users-add-email" />
            </div>
            <div className="space-y-2">
              <Label>Wachtwoord</Label>
              <Input type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} required data-testid="users-add-password" />
            </div>
            <div className="space-y-2">
              <Label>Rol</Label>
              <Select value={form.globale_rol} onValueChange={v => setForm({...form, globale_rol: v})}>
                <SelectTrigger data-testid="users-add-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {allowedRoles.map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label>
                Telefoonnummer <span className="text-slate-400 font-normal">(optioneel)</span>
              </Label>
              <Input
                type="tel"
                placeholder="+31 6 12345678"
                value={form.telefoon}
                onChange={e => setForm({...form, telefoon: e.target.value})}
                data-testid="users-add-telefoon"
                autoComplete="tel"
              />
            </div>
          </div>

          <div className="border-t border-slate-100 pt-3">
            <Label className="text-sm font-semibold text-[#1a2332] mb-2 block">Locaties</Label>
            <LocationsSelector
              locations={locations}
              selected={form.locations}
              role={form.globale_rol}
              onChange={(next) => setForm({...form, locations: next})}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} className="rounded-xl">Annuleren</Button>
            <Button type="submit" disabled={loading} className="bg-[#1a2332] hover:bg-[#111827] text-white rounded-xl" data-testid="users-add-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Toevoegen'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}


function EditUserDialog({ open, user, onClose, onSuccess, currentUserRole, locations }) {
  const [form, setForm] = useState({ naam: '', globale_rol: 'locatie', locations: [], telefoon: '', status: 'actief' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Confirmation modal when the save is about to deactivate the user.
  const [confirmDeactivate, setConfirmDeactivate] = useState(false);

  const myLevel = ROLE_LEVELS[currentUserRole] || 0;
  const allowedRoles = Object.entries(ROLE_LABELS).filter(([k]) => ROLE_LEVELS[k] < myLevel);

  // super_owner can never be deactivated (backend enforces too)
  const canToggleStatus = !!user && user.globale_rol !== 'super_owner';

  // Re-initialise form whenever a different user is targeted
  useEffect(() => {
    if (user) {
      setForm({
        naam: user.naam || '',
        globale_rol: user.globale_rol || 'locatie',
        locations: Array.isArray(user.locations) ? [...user.locations] : [],
        telefoon: user.telefoon || '',
        status: user.status || 'actief',
      });
      setError('');
      setConfirmDeactivate(false);
    }
  }, [user]);

  if (!user) return null;

  const statusChanged = form.status !== user.status;
  const willDeactivate = statusChanged && form.status === 'inactief';

  // Performs the actual save — called either directly or after confirmation.
  const doSave = async () => {
    setError('');
    setLoading(true);
    try {
      // 1) Update editable fields (naam, rol, locations, telefoon)
      const payload = {
        naam: form.naam,
        globale_rol: form.globale_rol,
        locations: form.locations,
        // Always send telefoon (even empty string) so the user can clear it
        // — backend strips and stores as null when blank.
        telefoon: form.telefoon ?? '',
      };
      const res = await usersAPI.update(user.id, payload);
      if (!res.ok) {
        const d = await res.clone().json();
        setError(d.detail || 'Fout bij opslaan');
        setLoading(false);
        return;
      }

      // 2) Status change via dedicated endpoints (kills sessions on deactivate).
      if (statusChanged) {
        const statusRes = form.status === 'inactief'
          ? await usersAPI.deactivate(user.id)
          : await usersAPI.activate(user.id);
        if (!statusRes.ok) {
          const d = await statusRes.clone().json().catch(() => ({}));
          setError(d.detail || 'Fout bij statuswijziging');
          setLoading(false);
          return;
        }
      }

      toast.success(
        statusChanged
          ? `${form.naam} bijgewerkt — nu ${form.status === 'actief' ? 'actief' : 'inactief'}`
          : `${form.naam} bijgewerkt`
      );
      setLoading(false);
      onSuccess();
    } catch {
      setError('Fout bij opslaan');
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Guard: deactivation is destructive-ish → confirm first.
    if (willDeactivate) {
      setConfirmDeactivate(true);
      return;
    }
    await doSave();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="users-edit-dialog">
        <DialogHeader>
          <DialogTitle>Gebruiker bewerken</DialogTitle>
          <DialogDescription className="sr-only">
            Pas naam, rol, status, telefoonnummer of locaties van deze gebruiker aan. E-mail is niet wijzigbaar.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && <p className="text-sm text-red-600 bg-red-50 p-2 rounded" data-testid="users-edit-error">{error}</p>}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Naam</Label>
              <Input value={form.naam} onChange={e => setForm({...form, naam: e.target.value})} required data-testid="users-edit-naam" />
            </div>
            <div className="space-y-2">
              <Label>E-mailadres</Label>
              <Input value={user.email} disabled className="bg-slate-50 text-slate-500" data-testid="users-edit-email" />
              <p className="text-[10px] text-slate-400">E-mail is niet wijzigbaar — voorkomt verwarring bij integraties.</p>
            </div>
            <div className="space-y-2">
              <Label>Rol</Label>
              <Select value={form.globale_rol} onValueChange={v => setForm({...form, globale_rol: v})}>
                <SelectTrigger data-testid="users-edit-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {/* Show current role (potentially equal to caller's) plus any below */}
                  {[...new Set([form.globale_rol, ...allowedRoles.map(([k]) => k)])].map(k => (
                    <SelectItem key={k} value={k}>{ROLE_LABELS[k] || k}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <div className={`flex items-center justify-between gap-3 rounded-lg border px-3 py-2.5 transition-colors ${
                form.status === 'actief'
                  ? 'bg-emerald-50/60 border-emerald-200'
                  : 'bg-slate-50 border-slate-200'
              } ${!canToggleStatus ? 'opacity-60' : ''}`}>
                <div className="min-w-0">
                  <p className={`text-sm font-medium ${form.status === 'actief' ? 'text-emerald-700' : 'text-slate-600'}`}>
                    {form.status === 'actief' ? 'Account actief' : 'Account inactief'}
                  </p>
                  <p className="text-[10px] text-slate-500 mt-0.5">
                    {canToggleStatus
                      ? (willDeactivate
                          ? 'Actieve sessies worden beëindigd bij opslaan.'
                          : 'Schakelaar wijzigt status; treedt in werking bij opslaan.')
                      : 'Super Owner kan niet gedeactiveerd worden.'}
                  </p>
                </div>
                <Switch
                  checked={form.status === 'actief'}
                  onCheckedChange={(v) => setForm({ ...form, status: v ? 'actief' : 'inactief' })}
                  disabled={!canToggleStatus}
                  data-testid="users-edit-status-switch"
                  className="data-[state=checked]:bg-emerald-600 data-[state=unchecked]:bg-slate-300"
                />
              </div>
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label>
                Telefoonnummer <span className="text-slate-400 font-normal">(optioneel)</span>
              </Label>
              <Input
                type="tel"
                placeholder="+31 6 12345678"
                value={form.telefoon}
                onChange={e => setForm({...form, telefoon: e.target.value})}
                data-testid="users-edit-telefoon"
                autoComplete="tel"
              />
            </div>
          </div>

          <div className="border-t border-slate-100 pt-3">
            <Label className="text-sm font-semibold text-[#1a2332] mb-2 block">Locaties</Label>
            <LocationsSelector
              locations={locations}
              selected={form.locations}
              role={form.globale_rol}
              onChange={(next) => setForm({...form, locations: next})}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} className="rounded-xl">Annuleren</Button>
            <Button type="submit" disabled={loading} className="bg-[#1a2332] hover:bg-[#111827] text-white rounded-xl" data-testid="users-edit-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Opslaan'}
            </Button>
          </DialogFooter>
        </form>

        {/* Nested confirmation when the save would deactivate the user. */}
        <AlertDialog open={confirmDeactivate} onOpenChange={setConfirmDeactivate}>
          <AlertDialogContent data-testid="users-edit-deactivate-confirm">
            <AlertDialogHeader>
              <AlertDialogTitle>Gebruiker deactiveren?</AlertDialogTitle>
              <AlertDialogDescription>
                <span className="font-medium text-[#1a2332]">{form.naam}</span> wordt op inactief gezet.
                Alle actieve sessies worden direct beëindigd en de gebruiker kan pas weer inloggen na opnieuw activeren.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="users-edit-deactivate-cancel">Annuleren</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => { setConfirmDeactivate(false); doSave(); }}
                className="bg-red-600 hover:bg-red-700"
                data-testid="users-edit-deactivate-confirm-button"
              >
                Deactiveren
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DialogContent>
    </Dialog>
  );
}
